import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteOutline, EditOutlined, Search, Sync } from '@mui/icons-material';
import './ExportGridView.css';

const defaultFormState = {
  full_name: '',
  role: '',
  location_id: '',
  date_of_overtime: '',
  hours_of_overtime: '',
  ch_rc_tc_ak_approved: '',
  processed_in_payroll: '',
  decimal_of_hour: '',
  minute_of_hour: '',
  comments: '',
};

const formatApprovedValue = (value) => (value === true ? 'Yes' : 'No');

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(numericValue);
};

const ManageOvertimePage = () => {
  const [syncing, setSyncing] = useState(false);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState('date_of_overtime');
  const [sortDirection, setSortDirection] = useState('desc');
  const [fullNameSearchInput, setFullNameSearchInput] = useState('');
  const [fullNameSearchQuery, setFullNameSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [formError, setFormError] = useState('');
  const [savingRecord, setSavingRecord] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState('');
  const hasAutoSyncedRef = useRef(false);

  const sortedLocations = useMemo(
    () => [...locations].sort((left, right) => (left?.name || left?.location_id || '').localeCompare(right?.name || right?.location_id || '')),
    [locations],
  );

  const sortedJobTitles = useMemo(
    () => [...jobTitles].sort((left, right) => (left?.label || '').localeCompare(right?.label || '')),
    [jobTitles],
  );

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('hv-toast', { detail: { message, severity } }));
  }, []);

  const loadOvertimeRecords = useCallback(async (pageIndex = page, nextSortKey = sortKey, nextSortDirection = sortDirection) => {
    setLoadingRecords(true);
    try {
      const response = await axiosInstance.get('/api/v1/overtime-report/records', {
        params: {
          page: pageIndex + 1,
          limit: rowsPerPage,
          sort_key: nextSortKey,
          sort_direction: nextSortDirection,
          full_name_search: fullNameSearchQuery || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          role: roleFilter || undefined,
          location_id: locationFilter || undefined,
        },
      });
      const responsePayload = response?.data || {};
      const payload = Array.isArray(responsePayload?.data)
        ? responsePayload
        : (responsePayload?.data || responsePayload || {});
      const nextRecords = Array.isArray(payload?.data) ? payload.data : [];
      setRecords(nextRecords);
      setTotalRecords(Number(payload?.total ?? nextRecords.length ?? 0));
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load overtime records.', 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [endDate, fullNameSearchQuery, locationFilter, page, roleFilter, rowsPerPage, showToast, sortDirection, sortKey, startDate]);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [locationsResponse, jobTitlesResponse] = await Promise.all([
        axiosInstance.get('/api/v1/location'),
        axiosInstance.get('/api/v1/manage/job-titles', { params: { page: 1, limit: 1000, sort_key: 'job_title', sort_direction: 'asc' } }),
      ]);
      const locationPayload = locationsResponse?.data?.data ?? locationsResponse?.data ?? [];
      const jobTitlePayload = jobTitlesResponse?.data?.data ?? [];
      setLocations(Array.isArray(locationPayload) ? locationPayload : []);
      setJobTitles(
        (Array.isArray(jobTitlePayload) ? jobTitlePayload : [])
          .map((entry) => ({
            value: entry?.normalized_job_title || '',
            label: entry?.job_title || entry?.normalized_job_title || '',
          }))
          .filter((entry) => entry.value && entry.label),
      );
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load overtime filter options.', 'error');
    } finally {
      setLoadingOptions(false);
    }
  }, [showToast]);

  const handleSyncOvertimeReport = useCallback(async (force = true, showSuccessToast = true) => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/api/v1/overtime-report/sync', { force });
      const payload = response?.data?.data || response?.data || {};

      const workbookName = payload?.workbook_name || 'overtime report';
      const sheetsSynced = Number(payload?.sheets_synced || 0);
      const recordsSaved = Number(payload?.records_saved || 0);
      if (showSuccessToast) {
        showToast(`Workbook: ${workbookName}\n${sheetsSynced} tab${sheetsSynced === 1 ? '' : 's'}\n${recordsSaved} row${recordsSaved === 1 ? '' : 's'}`, 'success');
      }
      setPage(0);
      await loadOvertimeRecords(0, sortKey, sortDirection);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to sync overtime report.', 'error');
    } finally {
      setSyncing(false);
    }
  }, [loadOvertimeRecords, showToast, sortDirection, sortKey]);

  useEffect(() => {
    if (hasAutoSyncedRef.current) return;
    hasAutoSyncedRef.current = true;
    handleSyncOvertimeReport(false, false);
  }, [handleSyncOvertimeReport]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadOvertimeRecords(page, sortKey, sortDirection);
  }, [loadOvertimeRecords, page, sortKey, sortDirection]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalRecords]);

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalRecords) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }
    const totalPages = Math.max(Math.ceil(totalRecords / rowsPerPage), 1);
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages);
    if (clampedPage !== page + 1) {
      setPage(clampedPage - 1);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleSortChange = (columnKey) => {
    setSortKey((current) => {
      if (current === columnKey) {
        setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection(columnKey === 'row' ? 'asc' : 'desc');
      }
      return columnKey;
    });
    setPage(0);
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  const applyFullNameSearch = useCallback(() => {
    setPage(0);
    setFullNameSearchQuery(fullNameSearchInput.trim());
  }, [fullNameSearchInput]);

  const handleFullNameSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applyFullNameSearch();
  };

  const clearFilters = () => {
    setFullNameSearchInput('');
    setFullNameSearchQuery('');
    setStartDate('');
    setEndDate('');
    setRoleFilter('');
    setLocationFilter('');
    setPage(0);
  };

  const formatDateInputValue = (value) => {
    if (!value) return '';
    const textValue = String(value).trim();
    const isoMatch = textValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const numericMatch = textValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
    if (!numericMatch) return '';
    let first = Number(numericMatch[1]);
    let second = Number(numericMatch[2]);
    let year = Number(numericMatch[3]);
    if (year < 100) year += year < 70 ? 2000 : 1900;

    let day = first;
    let month = second;
    if (first <= 12 && second > 12) {
      day = second;
      month = first;
    }
    if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return '';
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const openEditDialog = (record) => {
    setEditingRecordId(record?.id || '');
    setFormData({
      full_name: record?.full_name || '',
      role: record?.normalized_role || sortedJobTitles.find((jobTitle) => jobTitle.label === record?.role)?.value || '',
      location_id: record?.location_id || '',
      date_of_overtime: formatDateInputValue(record?.date_of_overtime),
      hours_of_overtime: record?.hours_of_overtime ?? '',
      ch_rc_tc_ak_approved: record?.ch_rc_tc_ak_approved === true ? 'Yes' : 'No',
      processed_in_payroll: formatDateInputValue(record?.processed_in_payroll),
      decimal_of_hour: record?.decimal_of_hour || '',
      minute_of_hour: record?.minute_of_hour || '',
      comments: record?.comments || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeEditDialog = () => {
    if (savingRecord) return;
    setDialogOpen(false);
    setEditingRecordId('');
    setFormError('');
    setFormData(defaultFormState);
  };

  const handleSaveRecord = async () => {
    if (!editingRecordId) return;
    if (!formData.full_name.trim() || !formData.role || !formData.location_id || !formData.date_of_overtime) {
      setFormError('Full name, role, location and date of overtime are required.');
      return;
    }

    setSavingRecord(true);
    setFormError('');
    try {
      const selectedRole = sortedJobTitles.find((jobTitle) => jobTitle.value === formData.role);
      const payload = {
        full_name: formData.full_name.trim(),
        role: selectedRole?.label || formData.role,
        location_id: formData.location_id,
        date_of_overtime: formData.date_of_overtime,
        hours_of_overtime: formData.hours_of_overtime,
        ch_rc_tc_ak_approved: formData.ch_rc_tc_ak_approved === 'Yes',
        processed_in_payroll: formData.processed_in_payroll,
        decimal_of_hour: formData.decimal_of_hour,
        minute_of_hour: formData.minute_of_hour,
        comments: formData.comments,
      };
      await axiosInstance.put(`/api/v1/overtime-report/records/${editingRecordId}`, payload);
      showToast('Overtime record updated successfully.', 'success');
      setDialogOpen(false);
      setEditingRecordId('');
      setFormData(defaultFormState);
      await loadOvertimeRecords(page, sortKey, sortDirection);
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to update overtime record.';
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSavingRecord(false);
    }
  };

  const openDeleteDialog = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingRecordId) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteRecord = async () => {
    const recordId = deleteTarget?.id || '';
    if (!recordId) return;

    setDeletingRecordId(recordId);
    try {
      await axiosInstance.delete(`/api/v1/overtime-report/records/${recordId}`);
      showToast('Overtime record deleted successfully.', 'success');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await loadOvertimeRecords(page, sortKey, sortDirection);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to delete overtime record.', 'error');
    } finally {
      setDeletingRecordId('');
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />;
  };

  const renderSortableHeader = (label, columnKey, sx = {}) => (
    <TableCell className={`sortableHeader ${sortKey === columnKey ? 'sorted' : ''}`} sx={{ fontWeight: 600, whiteSpace: 'nowrap', ...sx }}>
      <div className="headerContent" onClick={() => handleSortChange(columnKey)} role="button" tabIndex={0}>
        <strong>{label}</strong>
        {renderSortIcon(columnKey)}
      </div>
    </TableCell>
  );

  const formatOvertimeDate = (value) => {
    if (!value) return '-';
    const textValue = String(value).trim();
    const numericMatch = textValue.match(/^(\d{1,4})[/-](\d{1,2})[/-](\d{2,4})/);
    if (numericMatch) {
      let first = Number(numericMatch[1]);
      let second = Number(numericMatch[2]);
      let third = Number(numericMatch[3]);
      if (third < 100) {
        third += third < 70 ? 2000 : 1900;
      }

      let day;
      let month;
      let year;
      if (first > 31) {
        year = first;
        month = second;
        day = third;
      } else if (first > 12) {
        day = first;
        month = second;
        year = third;
      } else if (second > 12) {
        month = first;
        day = second;
        year = third;
      } else {
        day = first;
        month = second;
        year = third;
      }

      if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
      }
    }

    const date = new Date(textValue);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/London',
    }).format(date);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, boxSizing: 'border-box', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', textAlign: 'left' }}>
          <Box sx={{ minWidth: 220, textAlign: 'left' }}>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'left' }}>
              Overtime
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end" sx={{ flex: 1 }}>
            <TextField
              label="Search Name"
              size="small"
              placeholder="Search full name"
              value={fullNameSearchInput}
              onChange={(event) => setFullNameSearchInput(event.target.value)}
              onKeyDown={handleFullNameSearchKeyDown}
              sx={{ minWidth: 280 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="search overtime full name" edge="end" onClick={applyFullNameSearch}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Overtime Start"
              type="date"
              size="small"
              value={startDate}
              onChange={handleFilterChange(setStartDate)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Overtime End"
              type="date"
              size="small"
              value={endDate}
              onChange={handleFilterChange(setEndDate)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              select
              label="Role"
              size="small"
              value={roleFilter}
              onChange={handleFilterChange(setRoleFilter)}
              disabled={loadingOptions}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {sortedJobTitles.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label || option.value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Location"
              size="small"
              value={locationFilter}
              onChange={handleFilterChange(setLocationFilter)}
              disabled={loadingOptions}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Locations</MenuItem>
              {sortedLocations.map((option) => (
                <MenuItem key={option.location_id || option.name} value={option.location_id || ''}>
                  {option.name || option.location_id}
                </MenuItem>
              ))}
            </TextField>
            {loadingOptions && <CircularProgress size={20} />}
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!fullNameSearchInput && !fullNameSearchQuery && !startDate && !endDate && !roleFilter && !locationFilter}
              sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
              onClick={() => handleSyncOvertimeReport(true, true)}
              disabled={syncing}
              sx={{ minWidth: 220, whiteSpace: 'nowrap' }}
            >
              {syncing ? 'Syncing…' : 'Sync Overtime Sheet'}
            </Button>
          </Stack>
        </Box>

        <TableContainer sx={{ position: 'relative', maxHeight: '68vh', minHeight: !records.length ? 220 : 'auto', mt: 3, overflowX: 'auto', overflowY: 'auto' }}>
          {loadingRecords && records.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(1px)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Loading rows...
              </Typography>
            </Box>
          )}
          {!loadingRecords && !records.length && (
            <Box
              sx={{
                position: 'absolute',
                top: 49,
                left: 0,
                right: 0,
                bottom: 0,
                minHeight: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No overtime rows found. Sync the overtime sheet to load records.
              </Typography>
            </Box>
          )}
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1780,
              '& .MuiTableCell-sizeSmall': {
                padding: '12px 16px',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {renderSortableHeader('Full Name', 'full_name', { width: '12%', minWidth: 150 })}
                {renderSortableHeader('Role', 'role', { width: '11%', minWidth: 130 })}
                {renderSortableHeader('Site', 'site', { width: '10%', minWidth: 130 })}
                <TableCell sx={{ width: '10%', minWidth: 150, fontWeight: 600, whiteSpace: 'nowrap' }}><strong>Resolved Location</strong></TableCell>
                {renderSortableHeader('Date of overtime', 'date_of_overtime', { width: '10%', minWidth: 140 })}
                {renderSortableHeader('Hours', 'hours_of_overtime', { width: '7%', minWidth: 80 })}
                {renderSortableHeader('Hourly Rate', 'hourly_rate', { width: '8%', minWidth: 115 })}
                {renderSortableHeader('Total Amount', 'total_amount', { width: '8%', minWidth: 120 })}
                <TableCell sx={{ width: '8%', minWidth: 110, fontWeight: 600, whiteSpace: 'nowrap' }}><strong>Approved</strong></TableCell>
                {renderSortableHeader('Processed', 'processed_in_payroll', { width: '10%', minWidth: 140 })}
                <TableCell sx={{ width: '8%', minWidth: 110, fontWeight: 600, whiteSpace: 'nowrap' }}><strong>Decimal Hour</strong></TableCell>
                <TableCell sx={{ width: '8%', minWidth: 110, fontWeight: 600, whiteSpace: 'nowrap' }}><strong>Minute Hour</strong></TableCell>
                <TableCell sx={{ width: '15%', minWidth: 260, fontWeight: 600, whiteSpace: 'nowrap' }}><strong>Comments</strong></TableCell>
                <TableCell sx={{ width: '7%', minWidth: 90, fontWeight: 600, whiteSpace: 'nowrap' }} align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRecords && !records.length ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={4} />
                      <Typography color="text.secondary">Loading overtime rows...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : records.length > 0 ? (
                records.map((record) => (
                  <TableRow key={record?.id || `${record?.sheet_name}-${record?.row_number}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record?.full_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record?.role || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record?.site || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record?.location_name || record?.location_id || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatOvertimeDate(record?.date_of_overtime)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{record?.hours_of_overtime ?? '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(record?.hourly_rate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(record?.total_amount)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatApprovedValue(record?.ch_rc_tc_ak_approved)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatOvertimeDate(record?.processed_in_payroll)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{record?.decimal_of_hour || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{record?.minute_of_hour || '-'}</TableCell>
                    <TableCell
                      title={record?.comments || ''}
                      sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {record?.comments || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" color="primary" onClick={() => openEditDialog(record)} aria-label="edit overtime record">
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(record)} aria-label="delete overtime record">
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={14} sx={{ p: 0 }}>
                    <Box sx={{ minHeight: 140 }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
              label="Page"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              disabled={loadingRecords || !totalRecords}
            />
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `of ${Math.max(Math.ceil(totalRecords / rowsPerPage), 1)}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRecords)} of ${totalRecords}` : 'No records to show'}
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            showFirstButton
            showLastButton
            labelRowsPerPage=""
            labelDisplayedRows={() => ''}
            sx={{
              '.MuiTablePagination-toolbar': { p: 0 },
              '.MuiTablePagination-selectLabel': { display: 'none' },
              '.MuiTablePagination-displayedRows': { m: 0 },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Edit Overtime Record</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Full Name"
                value={formData.full_name}
                onChange={(event) => handleFormChange('full_name', event.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Role"
                value={formData.role}
                onChange={(event) => handleFormChange('role', event.target.value)}
                fullWidth
              >
                <MenuItem value="">Select Role</MenuItem>
                {formData.role && !sortedJobTitles.some((jobTitle) => jobTitle.label === formData.role) && (
                  <MenuItem value={formData.role} disabled>
                    {formData.role}
                  </MenuItem>
                )}
                {sortedJobTitles.map((jobTitle) => (
                  <MenuItem key={jobTitle.value} value={jobTitle.label}>
                    {jobTitle.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Location"
                value={formData.location_id}
                onChange={(event) => handleFormChange('location_id', event.target.value)}
                fullWidth
              >
                <MenuItem value="">Select Location</MenuItem>
                {sortedLocations.map((location) => (
                  <MenuItem key={location.location_id || location.name} value={location.location_id || ''}>
                    {location.name || location.location_id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Date of overtime"
                type="date"
                value={formData.date_of_overtime}
                onChange={(event) => handleFormChange('date_of_overtime', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Processed in Payroll"
                type="date"
                value={formData.processed_in_payroll}
                onChange={(event) => handleFormChange('processed_in_payroll', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Hours"
                value={formData.hours_of_overtime}
                onChange={(event) => handleFormChange('hours_of_overtime', event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Approved"
                value={formData.ch_rc_tc_ak_approved}
                onChange={(event) => handleFormChange('ch_rc_tc_ak_approved', event.target.value)}
                fullWidth
              >
                <MenuItem value="">Select Option</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
              <TextField
                label="Decimal Hour"
                value={formData.decimal_of_hour}
                onChange={(event) => handleFormChange('decimal_of_hour', event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Minute Hour"
                value={formData.minute_of_hour}
                onChange={(event) => handleFormChange('minute_of_hour', event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Comments"
              value={formData.comments}
              onChange={(event) => handleFormChange('comments', event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={savingRecord}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRecord} disabled={savingRecord}>
            {savingRecord ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Overtime Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the overtime record for {deleteTarget?.full_name || '-'} on {formatOvertimeDate(deleteTarget?.date_of_overtime)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingRecordId)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteRecord} disabled={Boolean(deletingRecordId)}>
            {deletingRecordId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageOvertimePage;
